import React from 'react';
import { Button } from '@/components/ui/button';
import { Pencil, Trash2 } from 'lucide-react';
import { useInteractionContext } from '../shared/rendering/InteractionContextRuntime';

interface TableActionsCellProps {
  tableId: string;
  rowId: string;
  rowData: Record<string, unknown>;
  actions?: string; // e.g., "edit,delete" or "edit" or "delete"
}

/**
 * Renders action buttons (edit/delete) for table rows
 * Used in the actions column of tables
 */
export const TableActionsCell: React.FC<TableActionsCellProps> = ({
  tableId,
  rowId,
  rowData,
  actions = 'edit,delete',
}) => {
  // Get interaction context
  const interaction = useInteractionContext();

  const actionList = actions.toLowerCase().split(',').map(a => a.trim());
  const hasEdit = actionList.includes('edit');
  const hasDelete = actionList.includes('delete');

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (interaction.handlers.onEditTableRow) {
      interaction.handlers.onEditTableRow(tableId, rowId, rowData);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (interaction.handlers.onDeleteTableRow) {
      interaction.handlers.onDeleteTableRow(tableId, rowId, rowData);
    }
  };

  // Only show in functional mode
  if (interaction.mode !== 'functional') {
    return <span className="text-xs text-muted-foreground">{actions}</span>;
  }

  return (
    <div className="flex items-center gap-2">
      {hasEdit && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleEdit}
          className="h-8 w-8 p-0"
          title="Edit row"
        >
          <Pencil className="h-4 w-4" />
        </Button>
      )}
      {hasDelete && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDelete}
          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
          title="Delete row"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};
